import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { TokenService } from './token.service';
import { environment } from '../../../../environments/environment';

interface RefreshResponse {
  access_token: string;
  sequence: string;
}

@Injectable({ providedIn: 'root' })
export class RefreshService {
  private http = inject(HttpClient);
  private tokens = inject(TokenService);

  private inFlight: Promise<void> | null = null;

  refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    const refreshToken = this.tokens.getRefreshToken();
    const sequence = this.tokens.getSequence();
    const claims = this.tokens.decode();
    if (!refreshToken) {
      this.tokens.clear();
      return Promise.reject(new Error('No refresh token'));
    }
    if (!sequence) {
      this.tokens.clear();
      return Promise.reject(new Error('No refresh sequence'));
    }
    if (!claims?.org_id || !claims.org_role_id) {
      this.tokens.clear();
      return Promise.reject(new Error('Access token has no organisation or role claims'));
    }
    // The API wants the membership (orgid, roleid) the access token represents,
    // the refresh token as `token` and the single-use `seq`, which rotates on
    // every refresh. The refresh token itself is not reissued.
    const params = new HttpParams().set('orgid', claims.org_id).set('roleid', claims.org_role_id);
    this.inFlight = firstValueFrom(
      this.http.post<RefreshResponse>(
        `${environment.apiUrl}/v2/authenticate/refresh`,
        { token: refreshToken, seq: sequence },
        { params },
      ),
    )
      .then((res) => {
        this.tokens.setAccessToken(res.access_token, res.sequence);
      })
      .catch((err) => {
        this.tokens.clear();
        throw err;
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }
}
